import { useState, useEffect } from 'react'
import {
  RefreshCw,
  TrendingUp,
  Image,
  Video,
  FileText,
  AlertCircle,
  Loader2,
  Star,
  Building2,
  Megaphone
} from 'lucide-react'
import { api } from '../../lib/api'
import { toast } from 'react-toastify'
import AdCard from './AdCard'

interface Competitor {
  id: string
  name: string
  domain: string
  isHeadToHead?: boolean
}

interface BrandInfo {
  id: string
  name: string
  domain: string
}

interface CompetitorAdMetrics {
  competitor: {
    id: string
    companyName: string
    domain: string
  }
  lastScrapedAt?: string
  totalAds: number
  activeAds?: number
  imageAdsCount?: number
  videoAdsCount?: number
  byPlatform?: Record<string, number>
}

interface AdScrapeJob {
  id: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  platform: string
  adsScraped: number
  adsNew: number
  errorMessage?: string
  createdAt: string
  completedAt?: string
}

interface CompetitorAdsTabProps {
  brandId: string
  brand?: BrandInfo
  competitors: Competitor[]
  isDemoMode?: boolean
}

export default function CompetitorAdsTab({ brandId, brand, competitors, isDemoMode = false }: CompetitorAdsTabProps) {
  // Only use head-to-head competitors
  const headToHeadCompetitors = competitors.filter(c => c.isHeadToHead)

  // "brand" means we're viewing the user's own brand ads
  const [selectedEntity, setSelectedEntity] = useState<string>(
    'brand' // Default to showing brand's own ads first
  )
  const [platform, setPlatform] = useState<'all' | 'GOOGLE' | 'META'>('all')
  const [metrics, setMetrics] = useState<{ totals: any; competitors: CompetitorAdMetrics[]; brand?: CompetitorAdMetrics } | null>(null)
  const [ads, setAds] = useState<any[]>([])
  const [jobs, setJobs] = useState<AdScrapeJob[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingAds, setLoadingAds] = useState(false)
  const [scraping, setScraping] = useState<string | null>(null)
  const [serviceConfigured, setServiceConfigured] = useState(true)

  useEffect(() => {
    checkServiceStatus()
    fetchMetrics()
    fetchJobs()
  }, [brandId])

  useEffect(() => {
    if (selectedEntity) {
      fetchAds(selectedEntity)
    }
  }, [selectedEntity, platform])

  const checkServiceStatus = async () => {
    if (isDemoMode) {
      setServiceConfigured(true)
      return
    }
    try {
      const response = await api.get('/competitive-ads/status')
      setServiceConfigured(response.data.configured)
    } catch (error) {
      console.error('Error checking service status:', error)
    }
  }

  const fetchMetrics = async () => {
    if (isDemoMode) {
      // Demo data
      setMetrics({
        totals: { totalAds: 180, activeAds: 105, imageAds: 80, videoAds: 28, competitorsTracked: headToHeadCompetitors.length + 1 },
        brand: brand ? {
          competitor: { id: 'brand', companyName: brand.name, domain: brand.domain },
          lastScrapedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
          totalAds: 24,
          activeAds: 16,
          imageAdsCount: 13,
          videoAdsCount: 5
        } : undefined,
        competitors: headToHeadCompetitors.map((c, i) => ({
          competitor: { id: c.id, companyName: c.name, domain: c.domain },
          lastScrapedAt: new Date(Date.now() - i * 86400000 * 7).toISOString(),
          totalAds: 30 + i * 20,
          activeAds: 20 + i * 10,
          imageAdsCount: 15 + i * 5,
          videoAdsCount: 5 + i * 3
        }))
      })
      setLoading(false)
      return
    }

    try {
      const response = await api.get('/competitive-ads/metrics?includeBrand=true')
      setMetrics(response.data)
    } catch (error) {
      console.error('Error fetching metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAds = async (entityId: string) => {
    if (isDemoMode) {
      setAds(generateDemoAds(entityId))
      return
    }

    setLoadingAds(true)
    try {
      const params: any = {}
      if (platform !== 'all') params.platform = platform

      // Use different endpoint for brand vs competitor
      const endpoint = entityId === 'brand'
        ? '/competitive-ads/brand'
        : `/competitive-ads/competitors/${entityId}`

      const response = await api.get(endpoint, { params })
      setAds(response.data.ads || [])
    } catch (error) {
      console.error('Error fetching ads:', error)
      toast.error('Failed to load ads')
    } finally {
      setLoadingAds(false)
    }
  }

  const fetchJobs = async () => {
    if (isDemoMode) return
    try {
      const response = await api.get('/competitive-ads/jobs')
      setJobs(response.data.jobs || [])
    } catch (error) {
      console.error('Error fetching jobs:', error)
    }
  }

  const handleScrape = async (entityId: string) => {
    if (isDemoMode) {
      toast.info('Scraping disabled in demo mode')
      return
    }

    setScraping(entityId)
    try {
      const payload = entityId === 'brand'
        ? { isBrand: true, platforms: ['GOOGLE', 'META'] }
        : { competitorId: entityId, platforms: ['GOOGLE', 'META'] }

      const response = await api.post('/competitive-ads/scrape', payload)

      const entityName = entityId === 'brand'
        ? brand?.name || 'your brand'
        : response.data.competitor?.companyName || 'competitor'
      toast.success(`Scraping started for ${entityName}`)
      pollJobStatus(response.data.jobId)
    } catch (error: any) {
      console.error('Error starting scrape:', error)
      toast.error(error.response?.data?.message || 'Failed to start scraping')
      setScraping(null)
    }
  }

  const handleScrapeAll = async () => {
    if (isDemoMode) {
      toast.info('Scraping disabled in demo mode')
      return
    }

    // Scrape brand first, then competitors
    if (brand) {
      await handleScrape('brand')
    }
    for (const comp of headToHeadCompetitors) {
      await handleScrape(comp.id)
    }
  }

  const pollJobStatus = async (jobId: string) => {
    const checkStatus = async () => {
      try {
        const response = await api.get(`/competitive-ads/jobs/${jobId}`)
        const job = response.data

        if (job.status === 'completed') {
          toast.success(`Scraping complete: ${job.adsNew} new ads found`)
          fetchMetrics()
          fetchJobs()
          setScraping(null)
          if (selectedEntity) {
            fetchAds(selectedEntity)
          }
          return
        } else if (job.status === 'failed') {
          toast.error(`Scraping failed: ${job.errorMessage || 'Unknown error'}`)
          fetchJobs()
          setScraping(null)
          return
        }

        setTimeout(checkStatus, 5000)
      } catch (error) {
        console.error('Error polling job status:', error)
        setScraping(null)
      }
    }

    setTimeout(checkStatus, 3000)
  }

  // Demo data generator
  const generateDemoAds = (competitorId: string) => {
    const platforms = ['GOOGLE', 'META'] as const
    const formats = ['IMAGE', 'VIDEO', 'TEXT', 'CAROUSEL'] as const
    const themes = ['Price focus', 'Quality', 'Innovation', 'Convenience', 'Trust']
    const personas = ['Small business owners', 'Enterprise IT', 'Marketers', 'Developers']

    return Array.from({ length: 12 }, (_, i) => ({
      id: `demo-${competitorId}-${i}`,
      platform: platforms[i % 2],
      format: formats[i % 4],
      status: i < 8 ? 'ACTIVE' : 'INACTIVE',
      headline: [
        'Transform Your Business Today',
        'The #1 Solution for Growth',
        'Save 50% This Month Only',
        'Trusted by 10,000+ Companies',
        'Start Your Free Trial',
        'AI-Powered Analytics',
        'Simple. Fast. Powerful.',
        'The Future of Marketing',
        'Grow Faster with Us',
        'Enterprise-Grade Security',
        'No Credit Card Required',
        'Join Industry Leaders'
      ][i],
      bodyText: 'Experience the next generation of business solutions. Our platform helps you achieve more with less effort.',
      ctaText: ['Learn More', 'Get Started', 'Try Free', 'Sign Up'][i % 4],
      destinationUrl: 'https://example.com',
      thumbnailUrl: i % 3 !== 0 ? `https://picsum.photos/400/300?random=${i}` : undefined,
      impressionsMin: 10000 + i * 5000,
      impressionsMax: 50000 + i * 10000,
      spendMin: i % 2 === 0 ? 500 + i * 100 : undefined,
      spendMax: i % 2 === 0 ? 2000 + i * 200 : undefined,
      firstSeenAt: new Date(Date.now() - i * 86400000 * 3).toISOString(),
      lastSeenAt: new Date().toISOString(),
      analysis: {
        messagingTheme: themes[i % themes.length],
        targetPersona: personas[i % personas.length],
        copyQualityScore: 70 + (i % 30)
      }
    }))
  }

  if (!serviceConfigured && !isDemoMode) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Apify Integration Not Configured</h3>
        <p className="text-gray-600 mb-4">
          Add your Apify API token to enable competitive ad tracking.
        </p>
        <code className="bg-gray-100 px-3 py-1 rounded text-sm">
          APIFY_API_TOKEN=your_token_here
        </code>
      </div>
    )
  }

  // Don't block if we have a brand - we can still show brand ads even without competitors
  if (!brand && headToHeadCompetitors.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <Star className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Head-to-Head Competitors</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Add head-to-head competitors in the AI Visibility tab to track their advertising.
          Click the star icon next to any competitor to mark them as head-to-head.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  // Get selected entity data
  const isBrandSelected = selectedEntity === 'brand'
  const selectedCompetitorData = isBrandSelected
    ? (brand ? { id: 'brand', name: brand.name, domain: brand.domain } : null)
    : headToHeadCompetitors.find(c => c.id === selectedEntity)
  const selectedMetrics = isBrandSelected
    ? metrics?.brand
    : metrics?.competitors?.find(c => c.competitor.id === selectedEntity)

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Competitor Ads"
          value={metrics?.totals?.totalAds || 0}
          icon={<FileText className="h-5 w-5 text-indigo-600" />}
        />
        <MetricCard
          title="Active Campaigns"
          value={metrics?.totals?.activeAds || 0}
          icon={<TrendingUp className="h-5 w-5 text-green-600" />}
        />
        <MetricCard
          title="Image Ads"
          value={metrics?.totals?.imageAds || 0}
          icon={<Image className="h-5 w-5 text-blue-600" />}
        />
        <MetricCard
          title="Video Ads"
          value={metrics?.totals?.videoAds || 0}
          icon={<Video className="h-5 w-5 text-purple-600" />}
        />
      </div>

      {/* Brand & Competitor Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">Ad Library</h3>
          </div>
          <button
            onClick={handleScrapeAll}
            disabled={!!scraping || isDemoMode}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {scraping ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh All Ads
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Your Brand - First Option */}
          {brand && (
            <button
              onClick={() => setSelectedEntity('brand')}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                selectedEntity === 'brand'
                  ? 'border-green-500 bg-green-50'
                  : 'border-green-200 hover:border-green-300 bg-white'
              }`}
            >
              <Building2 className={`h-5 w-5 ${selectedEntity === 'brand' ? 'text-green-600' : 'text-green-400'}`} />
              <div className="text-left">
                <div className="font-semibold text-gray-900">{brand.name}</div>
                <div className="text-xs text-green-600 font-medium">Your Brand</div>
              </div>
              <div className="text-right border-l pl-3">
                <div className="text-lg font-bold text-green-600">
                  {metrics?.brand?.totalAds || 0}
                </div>
                <div className="text-xs text-gray-500">ads</div>
              </div>
              {scraping === 'brand' && (
                <Loader2 className="h-4 w-4 animate-spin text-green-600" />
              )}
            </button>
          )}

          {/* Separator */}
          {brand && headToHeadCompetitors.length > 0 && (
            <div className="flex items-center px-2">
              <div className="h-8 w-px bg-gray-300" />
            </div>
          )}

          {/* Head-to-Head Competitors */}
          {headToHeadCompetitors.map((comp) => {
            const compMetrics = metrics?.competitors?.find(c => c.competitor.id === comp.id)
            const isSelected = selectedEntity === comp.id
            const isScraping = scraping === comp.id

            return (
              <button
                key={comp.id}
                onClick={() => setSelectedEntity(comp.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <Star className={`h-4 w-4 ${isSelected ? 'text-yellow-500 fill-yellow-500' : 'text-yellow-400 fill-yellow-400'}`} />
                <div className="text-left">
                  <div className="font-semibold text-gray-900">{comp.name}</div>
                  <div className="text-xs text-gray-500">{comp.domain}</div>
                </div>
                <div className="text-right border-l pl-3">
                  <div className="text-lg font-bold text-indigo-600">
                    {compMetrics?.totalAds || 0}
                  </div>
                  <div className="text-xs text-gray-500">ads</div>
                </div>
                {isScraping && (
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Recent Jobs (collapsed) */}
      {jobs.length > 0 && jobs.some(j => j.status === 'running' || j.status === 'pending') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Scraping in progress...</span>
          </div>
        </div>
      )}

      {/* Ads Grid */}
      {selectedEntity && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isBrandSelected ? 'Your Ads' : `Ads from ${selectedCompetitorData?.name}`}
              </h3>
              {selectedMetrics?.lastScrapedAt && (
                <p className="text-xs text-gray-500">
                  Last updated: {new Date(selectedMetrics.lastScrapedAt).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as any)}
                className="text-sm border-gray-300 rounded-md"
              >
                <option value="all">All Platforms</option>
                <option value="GOOGLE">Google Ads</option>
                <option value="META">Meta Ads</option>
              </select>
              <button
                onClick={() => handleScrape(selectedEntity)}
                disabled={!!scraping || isDemoMode}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium border rounded-lg disabled:opacity-50 ${
                  isBrandSelected
                    ? 'text-green-600 border-green-600 hover:bg-green-50'
                    : 'text-indigo-600 border-indigo-600 hover:bg-indigo-50'
                }`}
              >
                {scraping === selectedEntity ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </button>
            </div>
          </div>

          {loadingAds ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            </div>
          ) : ads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="mb-4">
                {isBrandSelected
                  ? 'No ads found for your brand yet.'
                  : 'No ads found for this competitor yet.'}
              </p>
              <button
                onClick={() => handleScrape(selectedEntity)}
                disabled={!!scraping || isDemoMode}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
                  isBrandSelected
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                <RefreshCw className="h-4 w-4" />
                Fetch Ads Now
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {ads.map((ad) => (
                <AdCard key={ad.id} ad={ad} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Simple metric card component
function MetricCard({ title, value, icon, subtitle }: {
  title: string
  value: number | string
  icon: React.ReactNode
  subtitle?: string
}) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {icon}
      </div>
    </div>
  )
}
